import { Box, Chip, Grid2 as Grid, Typography } from '@mui/material'
import React from 'react'

import { getStatusProps } from '../../utils/utility'

/**
 * Status - 显示状态的 Chip 组件
 * @param {Object} props
 * @param {string} props.status - 状态值，应该是 STATUS 中定义的值之一
 * @param {Object} [props.sx] - 额外的样式属性
 * @returns {JSX.Element}
 */
export const Status = ({ status, sx = {} }) => {
  const statusProps = getStatusProps(status)
  return (
    <Chip
      label={statusProps.label}
      size="small"
      color={statusProps.color}
      sx={{
        mt: 0.5,
        ...sx,
      }}
    />
  )
}

/**
 * InfoItem - 显示单个信息项的组件
 * @param {Object} props
 * @param {React.ReactNode} props.icon - 图标组件
 * @param {string} props.label - 标签文本
 * @param {string} props.value - 显示的值
 * @param {string} props.color - 颜色
 * @param {boolean} [props.hideLabel] - 是否隐藏标签
 * @returns {JSX.Element}
 */
export const InfoItem = ({ icon, label, value, color, hideLabel }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
    {icon}
    <Box>
      {!hideLabel && (
        <Typography variant="caption" color="text.secondary" align="left" display="block" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
          {label}
        </Typography>
      )}
      {color ? (
        <Chip label={value} color={color} size="small" />
      ) : (
        <Typography
          variant="body1"
          sx={{
            mt: hideLabel ? 0 : 0.5,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontWeight: 500,
            lineHeight: 1.6,
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  </Box>
)

/**
 * InfoList - 显示信息列表的组件
 * @param {Object} props
 * @param {string} props.title - 列表标题
 * @param {Array<Object>} props.items - 信息项数组，每项包含 icon, label, value, chipColor
 * @param {number} [props.columns=2] - 列数，默认为2
 * @param {boolean} [props.hideLabels] - 是否隐藏所有标签
 * @param {Object} [props.sx] - 额外的样式属性
 * @returns {JSX.Element}
 */
export const InfoList = ({ title, items, columns = 2, hideLabels = false, sx = {} }) => {
  // 计算每列应该显示的项目数
  const itemsPerColumn = Math.ceil(items.length / columns)

  // 将项目分成多列
  const columnItems = Array.from({ length: columns }, (_, columnIndex) => {
    const startIndex = columnIndex * itemsPerColumn
    return items.slice(startIndex, startIndex + itemsPerColumn)
  })

  return (
    <Box sx={sx}>
      {title && (
        <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'medium', color: 'text.secondary' }}>
          {title}
        </Typography>
      )}
      <Box
        sx={{
          pl: { xs: 0, sm: 2, md: 0, lg: 0 },
          pr: { xs: 0, sm: 2, md: 2 },
        }}
      >
        <Grid
          container
          spacing={0}
          justifyContent="space-between"
          sx={{
            maxWidth: '900px',
            mx: 'auto',
            '& .MuiGrid2-root': {
              minWidth: { xs: '100%', sm: `${90 / columns}%` },
              maxWidth: { xs: '100%', sm: `${95 / columns}%` },
            },
          }}
        >
          {columnItems.map((columnContent, columnIndex) => (
            <Grid key={columnIndex} size={{ xs: 12, sm: 12 / columns }}>
              {columnContent.map((item, itemIndex) => (
                <InfoItem key={itemIndex} {...item} hideLabel={hideLabels} />
              ))}
            </Grid>
          ))}
        </Grid>
      </Box>
    </Box>
  )
}
